// 월말 택배 리스트 — 택배 대상 CSV 내보내기 · 미배송 일괄 전환/복구 · 택배사 운송장 회신 엑셀 반영
import { useMemo, useRef, useState } from "react";
import { writeBatch, doc, deleteField } from "firebase/firestore";
import { isInRegion } from "@/lib/region";
import * as XLSX from "xlsx";
import { db } from "@/lib/firebase";
import { useStudents } from "@/hooks/useStudents";
import { fmtYearMonth, fmtHo } from "@/lib/format";
import type { Student } from "@/types";

import { plainError } from "@/lib/plainError";
// 단지 '차수/단지' 번호 추출("대우마리나 1차" → "1", "자이더샵 2단지" → "2").
function complexNo(s?: string): string {
  const m = String(s || "").match(/(\d)\s*(?:차|단지)/);
  return m ? m[1] : "";
}
// 학교 원본(raw)과 정제(refined)의 단지 차수/단지번호가 둘 다 명시됐는데 서로 다르면 오매칭(유수훈형 1차↔3차).
function rawRefinedMismatch(raw?: string, refined?: string): boolean {
  const a = complexNo(raw);
  const b = complexNo(refined);
  return !!a && !!b && a !== b;
}
// 가구 동일 판단용 전화 키(뒤 8자리 — 앞 0/국번 누락 대비)
function phoneKey8(phone?: string): string {
  const d = String(phone || "").split(",")[0].replace(/\D/g, "");
  return d.length >= 8 ? d.slice(-8) : d;
}
// 배달 건물 식별 키 — 도로명+건물번호 + 동 + 호 (표기차 무시, '어느 집인가'만 본다)
function buildingSig(s: Student): string {
  const base = String(s.refinedAddress || s.rawAddress || "").split("(")[0].split(",")[0];
  const m = base.match(/([가-힣A-Za-z0-9]*(?:대로|로|길)\s*\d+(?:번[가-힣]?길\s*\d+)?(?:-\d+)?)/);
  const road = (m ? m[1] : base).replace(/\s+/g, "");
  return `${road}|${s.buildingDong || ""}|${s.buildingHo || ""}`;
}
// 학생 문서 ID → 회신 매칭용 6자리 관리코드(#ABC123 형식). CSV 품명·메모에 심어 보내고,
// 택배사 회신 리스트에 그대로 돌아오면 이름·전화 없이도 확정 매칭한다(FNV-1a 해시, 내보내기·업로드 공용).
function shortCode(id: string): string {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0).toString(36).toUpperCase().padStart(7, "0").slice(-6);
}

// 운송장 배달주소 — refined가 이미 동/호를 포함하면 중복 추가하지 않는다.
function shipAddress(s: Student): string {
  const base = (s.refinedAddress || s.rawAddress || "").trim();
  const compact = base.replace(/\s/g, "");
  const dong = s.buildingDong ? `${s.buildingDong}동` : "";
  const ho = fmtHo(s.buildingHo);
  const parts = [base];
  if (dong && !compact.includes(dong.replace(/\s/g, ""))) parts.push(dong);
  if (ho && !compact.includes(ho.replace(/\s/g, ""))) parts.push(ho);
  return parts.filter(Boolean).join(" ");
}

/**
 * 월말 택배 리스트 — courierFlag=true인 학생들을 택배사 CSV 양식으로 내보낸다.
 * CJ대한통운/한진/로젠 공통 기본 포맷: 받는분, 전화번호, 우편번호, 주소, 품명, 수량, 메모
 * + 이번달 미배송(active·택배아님·이번달 배송기록 없음) 학생을 일괄 택배로 전환/복구.
 * + 택배사 회신 엑셀에서 운송장 번호를 매칭해 학생에 기록 — ①품명·메모에 심은 관리코드(#XXXXXX) 확정 매칭
 *   (연락처 없는 롯데 리스트·동명이인·지난달 소급 대응) → ②이름+전화 매칭(코드 없는 리스트 호환).
 */
export default function CourierList() {
  const { students, loading } = useStudents({ courier: true });
  const { students: all } = useStudents();
  const ym = fmtYearMonth();
  // 운송장 작업 월 — 기본은 현재월. 지난달 운송장을 올릴 땐 그 달로 바꿔서 업로드/표시한다.
  const [workYm, setWorkYm] = useState(ym);
  const monthOptions = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => fmtYearMonth(new Date(now.getFullYear(), now.getMonth() - i, 1)));
  }, []);
  const [resetting, setResetting] = useState(false);
  const [resetLog, setResetLog] = useState("");
  const [converting, setConverting] = useState(false);
  const [convLog, setConvLog] = useState("");
  // 방금 전환한 학생 id — 새로고침 전까지만 유지(복구 버튼 대상)
  const [lastConvertedIds, setLastConvertedIds] = useState<string[]>([]);
  const [trackLog, setTrackLog] = useState("");
  const [tracking, setTracking] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // 이번달 미배송 = active · 택배 아님 · 이번달 배송기록 없음 (기사 배송완료 기준과 동일)
  const undelivered = useMemo(
    () => all.filter((s) => s.status === "active" && !s.courierFlag && !(s.deliveredMonths || []).includes(ym)),
    [all, ym],
  );

  const convertUndelivered = async () => {
    if (!undelivered.length) return;
    if (!confirm(`이번달 미배송 ${undelivered.length}명을 전부 월말 택배로 전환합니다.\n계속할까요?`)) return;
    setConverting(true);
    setConvLog("전환 중…");
    try {
      const ids = undelivered.map((s) => s.id);
      for (let i = 0; i < ids.length; i += 450) {
        const batch = writeBatch(db);
        ids.slice(i, i + 450).forEach((id) => batch.update(doc(db, "students", id), { courierFlag: true, courierReason: "month" }));
        await batch.commit();
        setConvLog(`전환 중… ${Math.min(i + 450, ids.length)}/${ids.length}`);
      }
      setLastConvertedIds(ids);
      setConvLog(`✅ ${ids.length}명 택배 전환 완료 (필요 시 아래 복구 버튼으로 되돌리기 가능)`);
    } catch (e: any) {
      setConvLog(`❌ 오류: ${plainError(e)}`);
    } finally {
      setConverting(false);
    }
  };

  // 방금 전환한 건만 일반으로 복구 — 원래부터 택배인 학생은 건드리지 않음
  const restoreConverted = async () => {
    if (!lastConvertedIds.length) return;
    if (!confirm(`방금 전환한 ${lastConvertedIds.length}명을 일반(배송)으로 되돌립니다.\n계속할까요?`)) return;
    setConverting(true);
    setConvLog("복구 중…");
    try {
      const ids = lastConvertedIds;
      for (let i = 0; i < ids.length; i += 450) {
        const batch = writeBatch(db);
        ids.slice(i, i + 450).forEach((id) => batch.update(doc(db, "students", id), { courierFlag: false, courierReason: deleteField() }));
        await batch.commit();
        setConvLog(`복구 중… ${Math.min(i + 450, ids.length)}/${ids.length}`);
      }
      setConvLog(`✅ ${ids.length}명 일반 배송으로 복구 완료`);
      setLastConvertedIds([]);
    } catch (e: any) {
      setConvLog(`❌ 오류: ${plainError(e)}`);
    } finally {
      setConverting(false);
    }
  };

  // 새 달 배송 시작 — "이번 달만" 택배로 돌린 학생만 일반 배송으로 되돌린다(예행연습 02-13).
  //   · 항상 택배(courierReason "always" — 권역 밖·주소 못 찾음)는 그대로 둔다. 새 달에도 택배다.
  //   · 사유가 없는 옛 문서는 주소로 판단한다: 권역 안이면 "이번 달만", 밖이면 "항상".
  //   · 배송기록(deliveries)·월별 운송장(trackingByMonth)은 월로 분리 저장되므로 건드리지 않는다.
  //   · 배송 완료 판정은 deliveredMonths의 '해당월 포함' 여부라 새 달엔 자동으로 미배송으로 잡힌다.
  const isMonthOnly = (s: Student) =>
    s.courierReason === "month" || (!s.courierReason && isInRegion(`${s.refinedAddress || ""} ${s.rawAddress || ""}`, s.gu || ""));
  const resetTargets = useMemo(() => students.filter(isMonthOnly), [students]);
  const keepAlways = students.length - resetTargets.length;
  const [lastResetIds, setLastResetIds] = useState<string[]>([]); // 방금 되돌린 학생 — 새로고침 전까지 되돌리기 가능
  const resetNewMonth = async () => {
    const targets = resetTargets;
    if (!targets.length) {
      setResetLog(keepAlways ? `되돌릴 학생이 없습니다. 항상 택배 ${keepAlways}명은 새 달에도 택배입니다.` : "현재 택배 전환된 학생이 없습니다.");
      return;
    }
    if (!confirm(`이번 달만 택배로 돌린 ${targets.length}명을 일반 배송으로 되돌립니다.
항상 택배(권역 밖·주소 못 찾음) ${keepAlways}명은 그대로 둡니다.
배송기록·운송장은 보존됩니다. 새 달 배송을 시작할까요?`)) return;
    setResetting(true);
    setResetLog("되돌리는 중…");
    try {
      const ids = targets.map((s) => s.id);
      const done: string[] = []; // 묶음마다 누적 — 중간에 끊겨도 이미 되돌린 학생은 「방금 시작 되돌리기」 대상에 남는다(코덱스 검토)
      for (let i = 0; i < ids.length; i += 450) {
        const batch = writeBatch(db);
        const slice = ids.slice(i, i + 450);
        slice.forEach((id) => batch.update(doc(db, "students", id), { courierFlag: false, courierReason: deleteField() }));
        await batch.commit();
        done.push(...slice);
        setLastResetIds([...done]);
        setResetLog(`되돌리는 중… ${done.length}/${ids.length}`);
      }
      setResetLog(`✅ ${ids.length}명을 일반 배송으로 되돌렸습니다${keepAlways ? ` — 항상 택배 ${keepAlways}명은 유지` : ""}. 잘못 눌렀으면 「방금 시작 되돌리기」.`);
    } catch (e: any) {
      setResetLog(`❌ 오류: ${plainError(e)}`);
    } finally {
      setResetting(false);
    }
  };

  // 새 달 시작을 잘못 눌렀을 때 — 방금 되돌린 학생만 다시 "이번 달만 택배"로
  const undoReset = async () => {
    if (!lastResetIds.length) return;
    if (!confirm(`방금 일반 배송으로 되돌린 ${lastResetIds.length}명을 다시 택배로 돌립니다.
계속할까요?`)) return;
    setResetting(true);
    setResetLog("되돌리는 중…");
    try {
      const ids = lastResetIds;
      for (let i = 0; i < ids.length; i += 450) {
        const batch = writeBatch(db);
        ids.slice(i, i + 450).forEach((id) => batch.update(doc(db, "students", id), { courierFlag: true, courierReason: "month" }));
        await batch.commit();
      }
      setResetLog(`✅ ${ids.length}명을 다시 택배로 돌렸습니다.`);
      setLastResetIds([]);
    } catch (e: any) {
      setResetLog(`❌ 오류: ${plainError(e)}`);
    } finally {
      setResetting(false);
    }
  };

  // 이름+전화로 학생을 찾기 위한 인덱스. 전화는 숫자만/뒤 8자리 토큰으로 비교(앞 0 손실·국번 누락 대비).
  const phoneTokens = (phone?: string) => {
    const set = new Set<string>();
    (phone || "").split(",").forEach((p) => {
      const d = p.replace(/\D/g, "");
      if (d) { set.add(d); if (d.length >= 8) set.add(d.slice(-8)); }
    });
    return set;
  };
  const byName = useMemo(() => {
    const m = new Map<string, { s: Student; tokens: Set<string> }[]>();
    students.forEach((s) => {
      const key = (s.name || "").trim();
      if (!key) return;
      const arr = m.get(key) || [];
      arr.push({ s, tokens: phoneTokens(s.phone) });
      m.set(key, arr);
    });
    return m;
  }, [students]);

  // 관리코드 → 학생. 전체 학생 기준(택배 전환 여부·월과 무관)이라 지난달 회신도 코드만 있으면 매칭된다.
  // 해시 충돌로 후보가 2명 이상이면 매칭하지 않는다(잘못 붙이지 않기 — 함정 #5와 같은 원칙).
  const byCode = useMemo(() => {
    const m = new Map<string, Student[]>();
    all.forEach((s) => {
      const c = shortCode(s.id);
      m.set(c, [...(m.get(c) || []), s]);
    });
    return m;
  }, [all]);

  // 택배사 회신 엑셀 업로드 → ①관리코드 확정 매칭 → ②이름+전화 매칭 → trackingNo 기록
  const onTrackingFile = async (file: File) => {
    setTracking(true);
    setTrackLog("운송장 파일 읽는 중…");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows2d = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 });

      const cell = (v: any) => String(v ?? "").trim();
      const headerRow = rows2d.find(
        (r) => Array.isArray(r) && r.some((c) => /운송장|송장|운송번호/.test(cell(c))),
      );
      if (!headerRow) {
        setTrackLog("❌ '운송장(송장)번호' 열을 찾지 못했습니다. 헤더에 '운송장' 또는 '송장' 글자가 있는지 확인하세요.");
        return;
      }
      const headerIdx = rows2d.indexOf(headerRow);
      const headers = headerRow.map((h) => cell(h));
      const col = (names: string[]) => headers.findIndex((h) => names.some((n) => h.includes(n)));
      const idxName = col(["받는", "수령", "수하", "이름", "성명", "고객명"]);
      const idxPhone = col(["전화", "연락처", "휴대", "핸드폰", "폰"]);
      const idxTrack = col(["운송장", "송장", "운송번호"]);

      const updates: { id: string; trackingNo: string }[] = [];
      const failed: string[] = [];
      let ambiguous = 0;
      let codeMatched = 0;
      for (let i = headerIdx + 1; i < rows2d.length; i++) {
        const r = rows2d[i] || [];
        const trackingNo = cell(r[idxTrack]);
        if (!trackingNo) continue;
        // ① 관리코드 확정 매칭 — CSV 품명·메모에 심은 #코드가 회신의 어느 칸에든 돌아오면 그 학생으로 확정.
        //    연락처가 빠진 회신 리스트(롯데 등)·동명이인·지난달 소급 반영 모두 이 경로로 해결된다.
        const codeCell = r.map(cell).find((c) => /#[A-Za-z0-9]{6}(?![A-Za-z0-9])/.test(c));
        const code = codeCell?.match(/#([A-Za-z0-9]{6})(?![A-Za-z0-9])/)?.[1].toUpperCase();
        const codeHit = code ? byCode.get(code) : undefined;
        if (codeHit && codeHit.length === 1) {
          updates.push({ id: codeHit[0].id, trackingNo });
          codeMatched++;
          continue;
        }
        // ② 이름+전화 매칭 — 코드 없는 회신 리스트 호환(기존 방식)
        const name = cell(r[idxName]);
        if (!name) continue;
        const toks = phoneTokens(idxPhone >= 0 ? cell(r[idxPhone]) : "");
        const cands = byName.get(name) || [];
        let hit = cands.filter((c) => [...toks].some((t) => c.tokens.has(t)));
        // 전화가 엑셀에 없고 동명이인도 없으면 이름만으로 매칭
        if (hit.length === 0 && toks.size === 0 && cands.length === 1) hit = cands;
        if (hit.length === 1) updates.push({ id: hit[0].s.id, trackingNo });
        else if (hit.length > 1) { ambiguous++; failed.push(`${name}(중복)`); }
        else failed.push(name);
      }

      if (!updates.length) {
        setTrackLog(`⚠️ 매칭된 건이 없습니다. (실패 ${failed.length}건) 이름/전화 열이 맞는지 확인하세요.`);
        return;
      }
      setTrackLog(`${workYm} 운송장 기록 중… 0/${updates.length}`);
      for (let i = 0; i < updates.length; i += 450) {
        const batch = writeBatch(db);
        // 월별로 분리 저장 — trackingByMonth["YYYY-MM"]. 레거시 trackingNo도 함께 갱신(구화면 호환).
        updates.slice(i, i + 450).forEach((u) => batch.update(doc(db, "students", u.id), {
          [`trackingByMonth.${workYm}`]: u.trackingNo,
          trackingNo: u.trackingNo,
        }));
        await batch.commit();
        setTrackLog(`${workYm} 운송장 기록 중… ${Math.min(i + 450, updates.length)}/${updates.length}`);
      }
      const failMsg = failed.length
        ? ` · ⚠️ 미매칭 ${failed.length}건${ambiguous ? `(동명이인 ${ambiguous})` : ""}: ${failed.slice(0, 10).join(", ")}${failed.length > 10 ? " 외" : ""}`
        : "";
      setTrackLog(`✅ 운송장 ${updates.length}건 기록 완료(관리코드 매칭 ${codeMatched}건)${failMsg}`);
    } catch (e: any) {
      setTrackLog(`❌ 오류: ${plainError(e)}`);
    } finally {
      setTracking(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // 같은 전화(=같은 가구)인데 배달 건물이 갈리는 집 — 전체 active 학생 기준으로 탐지(남매가 택배·일반 섞여 있어도 잡힘).
  const siblingMismatchPhones = useMemo(() => {
    const groups = new Map<string, Set<string>>();
    all.forEach((s) => {
      if (s.status !== "active") return;
      const k = phoneKey8(s.phone);
      if (!k) return;
      const set = groups.get(k) || new Set<string>();
      set.add(buildingSig(s));
      groups.set(k, set);
    });
    const bad = new Set<string>();
    groups.forEach((sigs, k) => { if (sigs.size > 1) bad.add(k); });
    return bad;
  }, [all]);

  const rows = useMemo(
    () =>
      students.filter((s) => s.status !== "paused").map((s) => {
        // 발송 전 점검 사유: ① 학교원본↔정제 건물 불일치(오매칭) ② 같은 전화 가구인데 주소 갈림
        const reasons = [
          rawRefinedMismatch(s.rawAddress, s.refinedAddress) && "단지 차수 불일치",
          siblingMismatchPhones.has(phoneKey8(s.phone)) && "가족 주소 갈림",
        ].filter(Boolean) as string[];
        return {
          이름: s.name,
          전화번호: (s.phone || "").split(",")[0],
          우편번호: s.postalCode || "",
          // 지오코더 도로명주소(refinedAddress)엔 건물 동·호가 없으므로 별도 필드를 붙여 택배 배달가능 주소를 만든다.
          주소: shipAddress(s),
          // 품명·메모에 관리코드(#XXXXXX)를 심는다 — 택배사 회신 리스트에 돌아오면 확정 매칭용.
          품명: `멸균우유(${s.brand}) #${shortCode(s.id)}`,
          수량: s.boxesPerMonth || 1,
          운송장번호: s.trackingByMonth?.[workYm] || (workYm === ym ? s.trackingNo : "") || "",
          메모: `${`${s.school} / ${s.guardian}`.trim()} #${shortCode(s.id)}`,
          구: s.gu,
          점검: reasons.join(", "),
        };
      }),
    [students, siblingMismatchPhones, workYm, ym],
  );

  const flaggedCount = useMemo(() => rows.filter((r) => r.점검).length, [rows]);

  const download = () => {
    const header = Object.keys(rows[0] || { 이름: "", 전화번호: "", 우편번호: "", 주소: "", 품명: "", 수량: 0, 운송장번호: "", 메모: "", 구: "" });
    const bom = "﻿"; // 엑셀 한글 깨짐 방지
    const csv = bom + [header.join(","), ...rows.map((r) => header.map((h) => `"${String((r as any)[h] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `택배발송_${workYm}_${rows.length}건.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">📮 월말 택배 리스트</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="input max-w-[130px] text-sm"
            value={workYm}
            onChange={(e) => setWorkYm(e.target.value)}
            title="운송장 업로드·표시 대상 월"
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>{m}{m === ym ? " (이번달)" : ""}</option>
            ))}
          </select>
          <button
            className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-40"
            onClick={resetNewMonth}
            disabled={resetting || !resetTargets.length}
            title="이번 달만 택배로 돌린 학생을 일반 배송으로 되돌립니다. 권역 밖·주소 못 찾음(항상 택배)은 그대로. 배송기록·운송장은 보존"
          >
            🔄 새 달 배송 시작 (이번 달만 택배 {resetTargets.length}명 되돌림{keepAlways ? ` · 항상 ${keepAlways}명 유지` : ""})
          </button>
          {lastResetIds.length > 0 && (
            <button className="btn-secondary" onClick={undoReset} disabled={resetting}>
              ↩️ 방금 시작 되돌리기 ({lastResetIds.length}명)
            </button>
          )}
          <button
            className="btn-secondary"
            onClick={convertUndelivered}
            disabled={converting || !undelivered.length}
          >
            📦 미배송 전체 택배 전환 ({undelivered.length}명)
          </button>
          {lastConvertedIds.length > 0 && (
            <button className="btn-secondary" onClick={restoreConverted} disabled={converting}>
              ↩️ 방금 전환분 복구 ({lastConvertedIds.length}명)
            </button>
          )}
          <button className="btn-secondary" onClick={() => fileRef.current?.click()} disabled={tracking}>
            🧾 운송장 엑셀 반영
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onTrackingFile(f); }}
          />
          <button className="btn-primary" onClick={download} disabled={!rows.length}>택배 엑셀 파일 받기 ({rows.length}건)</button>
        </div>
      </div>
      <p className="text-sm text-slate-500">
        기사가 <strong>월말 택배로 체크</strong>한 건 모음. 택배사 송장 입력 양식에 그대로 붙여넣을 수 있습니다.
        품명·메모의 <strong>#관리코드</strong>가 택배사 회신 리스트에 돌아오면 연락처 없이도 확정 매칭됩니다
        (코드가 없는 리스트는 이름+전화로 매칭).
      </p>
      {/* 매달 1일 0시 자동 해제(대표 결정 Q27, 서버 resetMonthlyCourier) — 그 뒤엔 이번 달만 택배 학생이 이 목록에서 빠진다 */}
      <p className="text-sm text-amber-700">
        매달 1일 0시(한국시간)에 <strong>이번 달만 택배</strong> 학생은 자동으로 일반 배송으로 돌아갑니다(항상 택배는 그대로).
        택배 엑셀 파일은 말일까지 받아 두세요.
      </p>
      {resetLog && <p className="text-sm font-medium text-emerald-700">{resetLog}</p>}
      {convLog && <p className="text-sm font-medium text-slate-700">{convLog}</p>}
      {trackLog && <p className="text-sm font-medium text-slate-700">{trackLog}</p>}
      {flaggedCount > 0 && (
        <p className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          ⚠️ 주소 점검 필요 {flaggedCount}건 — 발송 전 확인하세요. (아래 표의 빨간 행 · 점검 열 참고)
          <br />
          <span className="font-normal text-red-600">
            "단지 차수 불일치" = 학교 원본의 1차/2차/3차와 정제 주소의 차수가 달라 다른 동으로 매칭됐을 수 있음 · "가족 주소 갈림" = 같은 전화번호인데 가족끼리 배달주소가 다름.
          </span>
        </p>
      )}

      <div className="card overflow-auto max-h-[65vh]">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-2">구</th>
              <th className="p-2">이름</th>
              <th className="p-2">전화</th>
              <th className="p-2">우편번호</th>
              <th className="p-2">주소</th>
              <th className="p-2">품명</th>
              <th className="p-2 text-right">수량</th>
              <th className="p-2">운송장번호</th>
              <th className="p-2">메모</th>
              <th className="p-2">점검</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="p-4 text-center text-slate-400">로딩…</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className={`border-t ${r.점검 ? "bg-red-50" : ""}`}>
                <td className="p-2">{r.구}</td>
                <td className="p-2 font-medium">{r.이름}</td>
                <td className="p-2">{r.전화번호}</td>
                <td className="p-2">{r.우편번호}</td>
                <td className="p-2 max-w-[400px] truncate">{r.주소}</td>
                <td className="p-2">{r.품명}</td>
                <td className="p-2 text-right">{r.수량}</td>
                <td className="p-2 font-mono">{r.운송장번호}</td>
                <td className="p-2 text-slate-500">{r.메모}</td>
                <td className="p-2 text-red-600 font-medium whitespace-nowrap">{r.점검 && `⚠️ ${r.점검}`}</td>
              </tr>
            ))}
            {!loading && !rows.length && (
              <tr><td colSpan={10} className="p-8 text-center text-slate-400">월말 택배로 체크된 건이 없습니다</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
